import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Send, Loader2, CheckCircle2, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { COMPANY } from "@/lib/company";
import { PageHero } from "@/components/site/PageHero";
import { MapEmbed } from "@/components/site/MapEmbed";

const EMPTY = { name: "", email: "", phone: "", subject: "", message: "", privacy_consent: false };
const inputCls = "w-full bg-white/[0.04] border border-white/15 focus:border-[#E50914] outline-none px-4 py-3 text-white placeholder:text-slate-500 transition-colors";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{label}</span>
    {children}
  </label>
);

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) {
      toast.error("Devi accettare l'informativa privacy");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero eyebrow="Contatti" title="Parliamo del tuo trasporto" text="Scrivici per informazioni, preventivi o assistenza sulle tue spedizioni." image="/images/hero.jpg" />
      <section className="bg-[#0B0C0E] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-8">
            <div className="flex gap-4" data-testid="contact-address">
              <MapPin className="h-6 w-6 text-[#E50914] shrink-0" />
              <div className="text-slate-300">
                <p className="font-display text-xl font-bold uppercase text-white">Sede</p>
                <p className="mt-1">{COMPANY.name}</p>
                <p>{COMPANY.street}</p>
                <p>{COMPANY.cap} {COMPANY.city} ({COMPANY.province}), {COMPANY.country}</p>
              </div>
            </div>
            <div className="flex gap-4" data-testid="contact-support-email">
              <Mail className="h-6 w-6 text-[#E50914] shrink-0" />
              <div className="text-slate-300">
                <p className="font-display text-xl font-bold uppercase text-white">Assistenza</p>
                <a href={`mailto:${COMPANY.supportEmail}`} className="mt-1 inline-block hover:text-white underline underline-offset-4 break-all" data-testid="contact-support-email-link">{COMPANY.supportEmail}</a>
              </div>
            </div>
            <div className="border border-white/10"><MapEmbed className="h-72" /></div>
          </div>
          <div className="lg:col-span-7">
            {sent ? (
              <div className="border border-emerald-500/30 bg-emerald-500/10 p-10" data-testid="contact-success">
                <CheckCircle2 className="h-10 w-10 text-emerald-400" />
                <h2 className="mt-4 font-display text-3xl font-bold uppercase text-white">Messaggio inviato</h2>
                <p className="mt-2 text-slate-300">Grazie per averci scritto. Ti risponderemo il prima possibile.</p>
                <button onClick={() => setSent(false)} className="mt-6 text-sm text-white underline underline-offset-4" data-testid="contact-new-message-btn">Invia un altro messaggio</button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-[#14161A] border border-white/10 p-6 sm:p-10 space-y-5" data-testid="contact-form">
                <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase text-white">Modulo di contatto</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Nome e cognome *"><input required minLength={2} value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" /></Field>
                  <Field label="Email *"><input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" /></Field>
                  <Field label="Telefono"><input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone-input" /></Field>
                  <Field label="Oggetto *"><input required minLength={2} value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject-input" /></Field>
                </div>
                <Field label="Messaggio *"><textarea required minLength={10} rows={6} value={form.message} onChange={set("message")} className={inputCls} data-testid="contact-message-input" /></Field>
                <label className="flex items-start gap-3 text-sm text-slate-400">
                  <input type="checkbox" checked={form.privacy_consent} onChange={set("privacy_consent")} className="mt-1 accent-[#E50914] h-4 w-4" data-testid="contact-privacy-checkbox" />
                  <span>Ho letto l'<Link to="/privacy" className="text-white underline underline-offset-4">informativa privacy</Link> e acconsento al trattamento dei dati per rispondere alla mia richiesta. *</span>
                </label>
                <button type="submit" disabled={sending} className="inline-flex items-center gap-2 bg-[#E50914] hover:bg-[#C10710] disabled:opacity-60 text-white px-8 py-4 font-display font-bold uppercase tracking-wider transition-colors" data-testid="contact-submit-btn">
                  {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Invia messaggio
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
